import ActionTypes from '../constants/ActionTypes';
import axiosInstance from '../helpers/refreshTokenMiddleware';
import BASE_URL from '../constants/Api';
import { format } from 'date-fns';
import { isChunkEligible, splitIntoQuarterWindowsBackward } from '../helpers/chunkUtils';

/*****HELPER*****/
// キーで gauge_data を統合: incoming が base を上書き
const mergeGaugeData = (base = {}, incoming = {}) => ({ ...base, ...incoming });

// 重複排除の一意キー: device_id + gauge_id + get_datetime
const mkItemKey = (it) => `${it.device_id}|${it.gauge_id}|${it.get_datetime}`;
const dedupeByTripleKey = (arr) => {
    const seen = new Set();
    const out = [];
    for (const it of arr) {
        const k = mkItemKey(it);
        if (!seen.has(k)) { seen.add(k); out.push(it); }
    }
    return out;
};


const sortByDatetimeDesc = (a, b) => {
    // get_datetime は通常 'yyyy/MM/dd HH:mm:ss'
    return new Date(b.get_datetime) - new Date(a.get_datetime);
};

/*****************************************/

//Fetch data filter searchlist
export const fetchFacilitiesRequest = () => ({
    type: ActionTypes.FETCH_FACILITIES_REQUEST,
});

export const fetchFacilitiesSuccess = (data) => ({
    type: ActionTypes.FETCH_FACILITIES_SUCCESS,
    payload: data,
});

export const fetchFacilitiesFailure = (error) => ({
    type: ActionTypes.FETCH_FACILITIES_FAILURE,
    payload: error,
});

export const fetchFacilities = (companyId, failureId = 0) => async (dispatch) => {
    dispatch(fetchFacilitiesRequest());
    try {
        const response = await axiosInstance.post(BASE_URL + '/top/searchlist', { company_id: String(companyId), failure_id: failureId });
        dispatch(fetchFacilitiesSuccess(response.data.respons));
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504 
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。' 
            : error.message;
        dispatch(fetchFacilitiesFailure(errorMessage));
    }
};

//Fetch ImageList and DataList
export const fetchImageListRequest = () => ({
    type: ActionTypes.FETCH_IMAGE_LIST_REQUEST,
});

export const fetchImageListSuccess = (data, gaugeData, failure_id, isEmpty, new_start_date) => ({
    type: ActionTypes.FETCH_IMAGE_LIST_SUCCESS,
    payload: { data, gaugeData , failure_id, isEmpty, new_start_date},
});


export const fetchImageListFailure = (error) => ({
    type: ActionTypes.FETCH_IMAGE_LIST_FAILURE,
    payload: error,
});

export const fetchImageList = (
    companyId,
    companyName,
    startDate,
    endDate,
    pageGroupNumber,
    searchKeys = [], 
    facilityId = null,
    deviceId = null,
    gaugeId = null,
    status = null,
    searchComment = null,
    threshold = null,
    failureId = 0,
    newStartDate = null
) => async (dispatch, getState) => {
    dispatch(fetchImageListRequest());
    try {
        // CHUNK モード
        const bigCompanies = getState()?.login?.user?.big_companies || [];
        const chunkMode = isChunkEligible(companyId, startDate, endDate, searchComment, status, bigCompanies, failureId);
        if (chunkMode) {
            // 現在のグループで使用する key
            const keyForGroup = pageGroupNumber === 1 ? null : (searchKeys?.[pageGroupNumber - 1] ?? null);
            const windows = splitIntoQuarterWindowsBackward(startDate, endDate);

            // === 並列ではなく逐次処理（sequential）===
            let allData = [];
            let allGauge = {};
            let finalFailureId = null;
            let finalNewStartDate = null;

            for (const [s, e] of windows) {
                const payload = {
                    company_id: String(companyId),
                    company_name: companyName,
                    failure_id: failureId,
                    start_date: format(s, 'yyyy/MM/dd'),
                    end_date: format(e, 'yyyy/MM/dd'),
                    key: keyForGroup, // ＜-- 重要
                };
                if (facilityId) payload.facility_id = String(facilityId);
                if (deviceId)   payload.device_id   = String(deviceId);
                if (gaugeId)    payload.gauge_id    = String(gaugeId);
                if (status !== null && status !== '')             payload.status = status;
                if (searchComment !== null && searchComment !== '') payload.search_comment = searchComment;
                if (threshold !== null && threshold !== '')       payload.threshold = threshold;

                // API を順番に呼び出す
                const resp = await axiosInstance.post(BASE_URL + '/top/datamanage', payload);

                const r = resp?.data?.respons || {};
                const rdata = r.data || [];
                const rgauge = r.gauge_data || {};
                const rFailureId = r.failure_id ?? null;
                const rNewStart  = r.new_start_date ?? null;

                // 結果を統合
                allData  = allData.concat(rdata);
                allGauge = mergeGaugeData(allGauge, rgauge);
                // 最後の値を採用（最初を保持しても可—UIには影響しない）
                finalFailureId   = rFailureId;
                finalNewStartDate = rNewStart;

                if (allData.length >= 100) {
                    break;
                }
            }

            // 重複排除＋ソート＋100件取得（10ページ分）—現在のグループ用
            allData = dedupeByTripleKey(allData).sort(sortByDatetimeDesc).slice(0, 100);

            const isEmpty = Array.isArray(allData) && allData.length === 0;
            dispatch(fetchImageListSuccess(allData, allGauge, finalFailureId, isEmpty, finalNewStartDate));
            return;
        }

        // === 通常モード ===
        const payload = {
            company_id: String(companyId),
            company_name: companyName,
            failure_id: failureId,
            start_date: format(startDate, 'yyyy/MM/dd'),
            end_date: format(endDate, 'yyyy/MM/dd'),
            key: pageGroupNumber === 1 ? null : searchKeys[pageGroupNumber - 1] || null
        };
        if (facilityId) payload.facility_id = String(facilityId);
        if (deviceId)   payload.device_id   = String(deviceId);
        if (gaugeId)    payload.gauge_id    = String(gaugeId);
        if (status !== null && status !== '')             payload.status = status;
        if (searchComment !== null && searchComment !== '') payload.search_comment = searchComment;
        if (threshold !== null && threshold !== '')       payload.threshold = threshold;
        if (pageGroupNumber > 1 && newStartDate) {
            payload.new_start_date = format(newStartDate, 'yyyy/MM/dd');
        }

        const response = await axiosInstance.post(BASE_URL + '/top/datamanage', payload);
        const data           = response?.data?.respons?.data || [];
        const gaugeData      = response?.data?.respons?.gauge_data || {};
        const failure_id     = response?.data?.respons?.failure_id;
        const new_start_date = response?.data?.respons?.new_start_date;
        const isEmpty = Array.isArray(data) && data.length === 0;

        dispatch(fetchImageListSuccess(data, gaugeData, failure_id, isEmpty, new_start_date));
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504 
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。' 
            : error.message;
        dispatch(fetchImageListFailure(errorMessage));
    }
};

//Fetch count data
export const fetchCountRequest = () => ({
    type: ActionTypes.FETCH_COUNT_REQUEST,
});

export const fetchCountSuccess = (data) => ({
    type: ActionTypes.FETCH_COUNT_SUCCESS,
    payload: data,
});

export const fetchCountFailure = (error) => ({
    type: ActionTypes.FETCH_COUNT_FAILURE,
    payload: error,
});

export const fetchCount = (
    companyId,
    companyName,
    startDate,
    endDate,
    facilityId = null,
    deviceId = null,
    gaugeId = null,
    status = null,
    searchComment = null,
    threshold = null,
    failureId = 0
) => async (dispatch, getState) => {
    dispatch(fetchCountRequest());
    try {
        const bigCompanies = getState()?.login?.user?.big_companies || [];

        const chunkMode = isChunkEligible(companyId, startDate, endDate, searchComment, status, bigCompanies, failureId);
        if (chunkMode) {
            const windows = splitIntoQuarterWindowsBackward(startDate, endDate);
            let totalCount = 0;
            let perBlockKeys = [];
            let rollingKey = null; 
            let rollingCount = null; 

            for (let idx = 0; idx < windows.length; idx++) {
                const [s, e] = windows[idx];

                const payload = {
                    company_id: String(companyId),
                    company_name: companyName,
                    failure_id: failureId,
                    start_date: format(s, 'yyyy/MM/dd'),
                    end_date: format(e, 'yyyy/MM/dd'),
                    index: idx,
                };

                if (idx > 0) {
                    if (rollingKey != null) payload.key = rollingKey;
                    if (rollingCount != null) payload.count = rollingCount;  
                }

                if (facilityId) payload.facility_id = String(facilityId);
                if (deviceId)   payload.device_id   = String(deviceId);
                if (gaugeId)    payload.gauge_id    = String(gaugeId);
                if (status !== null && status !== '')             payload.status = status;
                if (searchComment !== null && searchComment !== '') payload.search_comment = searchComment;
                if (threshold !== null && threshold !== '')       payload.threshold = threshold;

                const resp = await axiosInstance.post(BASE_URL + '/top/datamanage-count', payload);
                const r = resp?.data?.respons || {};
                totalCount = Number(r.count || 0);
                rollingCount = (r.count ?? null);

                const keys = Array.isArray(r.keys) ? r.keys : [];
                perBlockKeys.push(keys);

                // 次ブロック用の key ＝ keys の最後の要素（空なら null）
                rollingKey = keys.length > 0 ? keys[keys.length - 1] : null;

                const mergedKeysFlat = perBlockKeys.flat();
                if (mergedKeysFlat.length >= 100) {
                    break;
                }
            }

            // 新: keys をブロック逆順で結合
            const mergedKeysRaw = [];
            for (let i = 0; i < perBlockKeys.length; i++) {
                mergedKeysRaw.push(...perBlockKeys[i]);
            }

            const noNulls = mergedKeysRaw.filter(k => k != null);

            const seen = new Set();
            const orderedUnique = noNulls.filter(k => {
                const sig = typeof k === 'string' ? k : JSON.stringify(k);
                if (seen.has(sig)) return false;
                seen.add(sig);
                return true;
            });

            const mergedKeys = [null, ...orderedUnique];

            const merged = { count: totalCount, keys: mergedKeys };
            dispatch(fetchCountSuccess(merged));
            return merged;
        }

        // === NORMAL MODE ===
        const payload = {
            company_id: String(companyId),
            company_name: companyName,
            failure_id: failureId,
            start_date: format(startDate, 'yyyy/MM/dd'),
            end_date: format(endDate, 'yyyy/MM/dd')
        };
        if (facilityId) payload.facility_id = String(facilityId);
        if (deviceId)   payload.device_id   = String(deviceId);
        if (gaugeId)    payload.gauge_id    = String(gaugeId);
        if (status !== null && status !== '')             payload.status = status;
        if (searchComment !== null && searchComment !== '') payload.search_comment = searchComment;
        if (threshold !== null && threshold !== '')       payload.threshold = threshold;

        const response = await axiosInstance.post(BASE_URL + '/top/datamanage-count', payload);
        const respons = response.data.respons || {};
        dispatch(fetchCountSuccess(respons));
        return respons;
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504 
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。' 
            : error.message;
        dispatch(fetchCountFailure(errorMessage));
    }
};

export const setSelectedItem = (item) => ({
    type: ActionTypes.SET_SELECTED_ITEM,
    payload: item,
});

//Data register
export const dataRegisterRequest = () => ({
    type: ActionTypes.DATA_REGISTER_REQUEST,
});

export const dataRegisterSuccess = (data) => ({
    type: ActionTypes.DATA_REGISTER_SUCCESS,
    payload: data
});

export const dataRegisterFailure = (error) => ({
    type: ActionTypes.DATA_REGISTER_FAILURE,
    payload: error,
});

export const dataRegister = (data) => async (dispatch) => {
    dispatch(dataRegisterRequest());
    try {
        const response = await axiosInstance.post(`${BASE_URL}/top/data-regist`, data);
        if (response.status === 200) {
            dispatch(dataRegisterSuccess(response.data));
            return response.data;
        } else {
            dispatch(dataRegisterFailure('Failed to register data'));
            return response.data;
        }
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。'
            : error.message;
        dispatch(dataRegisterFailure(errorMessage));
    }
};

//Data edit
export const dataEditRequest = () => ({
    type: ActionTypes.DATA_EDIT_REQUEST,
});

export const dataEditSuccess = (data) => ({
    type: ActionTypes.DATA_EDIT_SUCCESS,
    payload: data,
});

export const dataEditFailure = (error) => ({
    type: ActionTypes.DATA_EDIT_FAILURE,
    payload: error,
});

export const dataEdit = (data) => async (dispatch) => {
    dispatch(dataEditRequest());
    try {
        const response = await axiosInstance.post(`${BASE_URL}/top/data-edit`, data);
        if (response.status === 200) {
            dispatch(dataEditSuccess(response.data));
            return response.data;
        } else {
            dispatch(dataEditFailure('Failed to edit data'));
        }
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。'
            : error.message;
        dispatch(dataEditFailure(errorMessage));
    }
};

// Data delete
export const dataDeleteRequest = () => ({
    type: ActionTypes.DATA_DELETE_REQUEST,
});

export const dataDeleteSuccess = (data) => ({
    type: ActionTypes.DATA_DELETE_SUCCESS,
    payload: data,
});

export const dataDeleteFailure = (error) => ({
    type: ActionTypes.DATA_DELETE_FAILURE,
    payload: error,
});

export const dataDelete = (data) => async (dispatch) => {
    dispatch(dataDeleteRequest());
    try {
        const response = await axiosInstance.post(`${BASE_URL}/top/data-delete`, data);
        if (response.status === 200) {
            dispatch(dataDeleteSuccess(response.data));
            return response.data;
        } else {
            dispatch(dataDeleteFailure('Failed to delete data'));
            return null;
        }
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。'
            : error.message;
        dispatch(dataDeleteFailure(errorMessage));
        return null;
    }
};

//Fetch Camera Info
export const fetchCameraInfoRequest = () => ({
    type: ActionTypes.FETCH_CAMERA_INFO_REQUEST,
});

export const fetchCameraInfoSuccess = (camera_info) => ({
    type: ActionTypes.FETCH_CAMERA_INFO_SUCCESS,
    payload: camera_info,
});

export const fetchCameraInfoFailure = (error) => ({
    type: ActionTypes.FETCH_CAMERA_INFO_FAILURE,
    payload: error,
});

export const clearCameraInfo = () => ({
    type: ActionTypes.CLEAR_CAMERA_INFO,
});

export const fetchCameraInfo = (
    companyId,
    deviceId
) => async (dispatch) => {
    dispatch(fetchCameraInfoRequest());
    try {      
        if( !companyId || !deviceId) return;
                    
        const payload = {
            company_id: String(companyId),
            device_id:  String(deviceId)
        };

        const response = await axiosInstance.post(BASE_URL + '/top/camera-info', payload);
          
        const cameraInfo = response?.data?.respons;
        dispatch(fetchCameraInfoSuccess(cameraInfo));
        
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504 
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。' 
            : error.message;
        dispatch(fetchCameraInfoFailure(errorMessage));
    }
};

//遠隔撮影
export const photoShootRequest = () => ({
    type: ActionTypes.PHOTO_SHOOT_REQUEST,
});

export const photoShootSuccess = (data) => ({
    type: ActionTypes.PHOTO_SHOOT_SUCCESS,
    payload: data,
});

export const photoShootFailure = (error) => ({
    type: ActionTypes.PHOTO_SHOOT_FAILURE,
    payload: error,
});

export const onPhotoShoot = (
    companyId,    
    cameraId,
    deviceId
) => async (dispatch) => {
    dispatch(photoShootRequest());
    try { 
        const payload = { 
            company_id: String(companyId),     
            camera_id: String(cameraId),
            device_id: String(deviceId)
        };

        const response = await axiosInstance.post(`${BASE_URL}/top/photo-shoot`, payload);
        if (response.status === 200) {
            const photoshoot_info = response?.data?.respons.photoshoot_info;
            dispatch(photoShootSuccess(photoshoot_info));
            return response.data;
        } else {
            dispatch(photoShootFailure('Failed to execute photo shoot'));
        }
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。'
            : error.message;
        dispatch(photoShootFailure(errorMessage));
    }
};

//遠隔撮影ステータスを取得
export const fetchPhotoShootStatusRequest = () => ({
    type: ActionTypes.FETCH_PHOTO_SHOOT_STATUS_REQUEST,
});

export const fetchPhotoShootStatusSuccess = (status) => ({
    type: ActionTypes.FETCH_PHOTO_SHOOT_STATUS_SUCCESS,
    payload: status,
});

export const fetchPhotoShootStatusFailure = (error) => ({
    type: ActionTypes.FETCH_PHOTO_SHOOT_STATUS_FAILURE,
    payload: error,
});

export const fetchPhotoShootStatus = (
    cameraId,
    taskId
) => async (dispatch) => {
    dispatch(fetchPhotoShootStatusRequest());
    try {      
        if( !cameraId || !taskId) return;
                    
        const payload = {
            camera_id: String(cameraId),
            task_id:  taskId
        };

        const response = await axiosInstance.post(
            BASE_URL + '/top/photo-shoot-status', 
            payload
        );

        // console.log('action - fetchPhotoShootStatus() - response:', response);

        const status = response?.data?.respons;        
        dispatch(fetchPhotoShootStatusSuccess(status));
        
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504 
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。' 
            : error.message;
        dispatch(fetchCameraInfoFailure(errorMessage));
    }
};

//遠隔撮影結果をシステムへ更新
export const photoShootUpdateRequest = () => ({
    type: ActionTypes.PHOTO_SHOOT_UPDATE_REQUEST,
});

export const photoShootUpdateSuccess = (data) => ({
    type: ActionTypes.PHOTO_SHOOT_UPDATE_SUCCESS,
    payload: data,
});

export const photoShootUpdateFailure = (error) => ({
    type: ActionTypes.PHOTO_SHOOT_UPDATE_FAILURE,
    payload: error,
});

export const onPhotoShootUpdate = (
    companyId,    
    cameraId,
    deviceId,
    gaugeId
) => async (dispatch) => {
    dispatch(photoShootUpdateRequest());
    try { 
        const payload = { 
            company_id: String(companyId),     
            camera_id: String(cameraId),
            device_id: String(deviceId),
            gauge_id: String(gaugeId)
        };

        const response = await axiosInstance.post(`${BASE_URL}/top/photo-shoot-update`, payload);
        if (response.status === 200) {
            const result = response?.data?.respons;
            dispatch(photoShootUpdateSuccess(result));
            return response.data;
        } else {
            dispatch(photoShootUpdateFailure('Failed to update photo shoot'));
        }
    } catch (error) {
        const errorMessage = error.response && error.response.status === 504
            ? '504 ゲートウェイタイムアウト。\n後でもう一度お試しください。'
            : error.message;
        dispatch(photoShootUpdateFailure(errorMessage));
    }
};

export const setRelativeActive = (index) => ({
    type: ActionTypes.SET_RELATIVE_ACTIVE,
    payload: index,
});
